import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputMask, InputMaskChangeEvent } from 'primereact/inputmask';
import { OverlayPanel } from 'primereact/overlaypanel';
import {
  FieldErrors,
  FieldValues,
  Path,
  UseFormSetValue
} from 'react-hook-form';

import { formatDateStringFromRaw, parseInputDate } from '../../utils/date-utils';
import './styles/DateTimeInput.css';

export type CalendarOnChangeEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Calendar>['onChange']>
>[0];

interface DateTimeInputProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  setValue: UseFormSetValue<TFormValues>;
  errors: FieldErrors<TFormValues>;
  initialValue?: string;
  disabled?: boolean;
  passedClassName?: string;
  onChangeHandler?: (e: CalendarOnChangeEvent, fieldName?: string) => void;
  isRegistration?: boolean;
  plannedEndDate?: boolean;
  manualInput?: boolean;
}

export const DateTimeInput = <TFormValues extends FieldValues>({
  name,
  setValue,
  errors,
  initialValue,
  disabled,
  passedClassName,
  onChangeHandler,
  isRegistration,
  plannedEndDate = false,
  manualInput = true,
}: DateTimeInputProps<TFormValues>) => {
  const [inputValue, setInputValue] = useState(initialValue ?? '');
  const [wasDateSet, setWasDateSet] = useState(false);
  const [calendarDate, setCalendarDate] = useState<Date | null>(null);

  const [hours, setHours] = useState<string>('0');
  const [minutes, setMinutes] = useState<string>('0');

  const op = useRef<OverlayPanel>(null);
  const maskRef = useRef<InputMask>(null);
  const isProgrammaticChange = useRef(false);

  const errorMessage = (errors as Record<string, any>)[name]?.message;

  useEffect(() => {
    if (initialValue) {
      const date = new Date(initialValue);
      if (!isNaN(date.getTime())) {
        const formatted = formatDateStringFromRaw(initialValue);
        setInputValue(formatted);
        setCalendarDate(date);
        setWasDateSet(true);
        setHours(date.getHours().toString().padStart(2, '0'));
        setMinutes(date.getMinutes().toString().padStart(2, '0'));
      }
    }
  }, [initialValue]);

  const handleClear = () => {
    setInputValue('');
    setCalendarDate(null);
    setHours('');
    setMinutes('');

    const clearEvent = { value: null } as CalendarOnChangeEvent;
    if (onChangeHandler) {
      onChangeHandler(clearEvent, name as string);
    } else {
      setValue(name, '' as any, { 
        shouldDirty: true,
        shouldValidate: true 
      });
    }
  };

  const handleMaskComplete = (e: any) => {
    const value = e.value;
    if (!value) {
      handleClear();
      return;
    }

    const parsedDate = parseInputDate(value);
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      setCalendarDate(parsedDate);
      setHours(parsedDate.getHours().toString().padStart(2, '0'));
      setMinutes(parsedDate.getMinutes().toString().padStart(2, '0'));

      const event = { value: parsedDate } as CalendarOnChangeEvent;
      if (onChangeHandler) {
        onChangeHandler(event, name as string);
      } else {
        setValue(name, parsedDate.toISOString() as any);
      }
    }
  };

  const handleChange = (e: InputMaskChangeEvent) => {
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }

    setInputValue(e.value || '');

    if ((e.value || '').replace(/_/g, '').length === 16) {
      setWasDateSet(true);
      handleMaskComplete(e);
    }
  };

  const handleCalendarChange = (e: CalendarOnChangeEvent) => {
    isProgrammaticChange.current = true;
    const date = e.value;

    if (date === null) {
      handleClear();
      op.current?.hide();
      return;
    }

    if (date instanceof Date && !isNaN(date.getTime())) {
      date.setHours(hours ? parseInt(hours, 10) : 0);
      date.setMinutes(minutes ? parseInt(minutes, 10) : 0);

      setCalendarDate(date);
      setWasDateSet(true);
      setInputValue(formatDateStringFromRaw(date.toISOString()));

      if (onChangeHandler) {
        onChangeHandler({ ...e, value: date }, name as string);
      } else {
        setValue(name, date.toISOString() as any, { 
          shouldDirty: true,
          shouldValidate: true 
        });
      }
    }
  };

  const makeTimeChangeHandler = (
    max: number,
    setter: (v: string) => void,
    getOtherValue: () => string,
    applyToDate: (num: number, date: Date) => void
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (!/^\d*$/.test(val)) return;
    if (val.length > 2) val = val.slice(0, 2);

    let num: number | null = null;
    if (val.length === 2) {
      const parsed = parseInt(val, 10);
      if (!isNaN(parsed)) {
        num = parsed;
        if (num > max) {
          num = max;
        }
        val = num.toString().padStart(2, '0');
      }
    }

    setter(val);

    if (num !== null && calendarDate) {
      const newDate = new Date(calendarDate);
      applyToDate(num, newDate);
      const other = getOtherValue();
      if (other) {
        const otherNum = parseInt(other, 10);
        if (!isNaN(otherNum)) {
          if (max === 59) {
            newDate.setHours(hours ? parseInt(hours, 10) : 0);
          } else {
            newDate.setMinutes(minutes ? parseInt(minutes, 10) : 0);
          }
        }
      }
      setCalendarDate(newDate);
      setValue(name, newDate.toISOString() as any, { shouldDirty: true, shouldValidate: true });
    }
  };

  const footerTemplate = () =>
    !manualInput ? (
      <div>
        <div className="flex flex-col gap-2 p-2">
          <div className="flex gap-2 align-items-center">
            <input
              type="text"
              inputMode="numeric"
              pattern="\\d*"
              value={hours}
              maxLength={2}
              onChange={makeTimeChangeHandler(
                23,
                (v) => setHours(v),
                () => minutes,
                (num, d) => d.setHours(num)
              )}
              className="w-16 p-inputtext p-component"
            />
            :
            <input
              type="text"
              inputMode="numeric"
              pattern="\\d*"
              value={minutes}
              maxLength={2}
              onChange={makeTimeChangeHandler(
                59,
                (v) => setMinutes(v),
                () => hours,
                (num, d) => d.setMinutes(num)
              )}
              className="w-16 p-inputtext p-component"
            />
          </div>
        </div>
        <div className="flex p-2 justify-content-between">
          <Button
            label="Текущее время"
            size="small"
            text
            onClick={() => {
              const now = new Date();
              const hh = now.getHours().toString().padStart(2, '0');
              const mm = now.getMinutes().toString().padStart(2, '0');
              setHours(hh);
              setMinutes(mm);
              setCalendarDate(now);
              setValue(name, now.toISOString() as any, { 
                shouldDirty: true, 
                shouldValidate: true 
              });
              if (onChangeHandler) {
                const event = { value: now } as CalendarOnChangeEvent;
                onChangeHandler(event, name as string);
              }
            }}
          />
          <Button
            label="Очистить"
            size="small"
            text
            onClick={() => {
              handleClear();
              op.current?.hide();
            }}
          />
        </div>
        <div className="p-2">
          <Button
            label="Готово"
            onClick={() => op.current?.hide()}
            text
            size="small"
          />
        </div>
      </div>
    ) : (
      <Button label="Готово" onClick={() => op.current?.hide()} />
    );

  return (
    <div>
      <div className="p-inputgroup">
        {manualInput ? (
          <InputMask
            ref={maskRef}
            mask="99.99.9999 99:99"
            defaultValue={initialValue ? formatDateStringFromRaw(initialValue) : ''}
            onComplete={handleMaskComplete}
            onChange={handleChange}
            className={`dateTimeInputMask ${errorMessage ? 'p-invalid' : ''} ${passedClassName || ''}`.trim()}
            slotChar="дд.мм.гггг чч:мм"
            placeholder="дд.мм.гггг чч:мм"
            disabled={disabled}
            style={{height: 42}}
          />
        ) : (
          <input
            type="text"
            value={
              calendarDate
                ? `${calendarDate.toLocaleDateString()} ${calendarDate
                    .getHours()
                    .toString()
                    .padStart(2, '0')}:${calendarDate
                    .getMinutes()
                    .toString()
                    .padStart(2, '0')}`
                : ''
            }
            readOnly
            className={`p-inputtext ${errorMessage ? 'p-invalid' : ''} ${passedClassName || ''}`.trim()}
            disabled={disabled}
            onClick={(e) => op.current?.toggle(e)}
            style={{ padding: '11.7', height: 42 }}
          />
        )}
      </div>

      <OverlayPanel
        ref={op}
        className="DateTimeInput DateTimeInput-op"
        dismissable
        appendTo={document.body}
      >
        <Calendar
          value={calendarDate}
          onChange={handleCalendarChange}
          inline
          dateFormat="dd.mm.yy"
          footerTemplate={footerTemplate}
          style={{ width: '357px' }}
        />
      </OverlayPanel>
    </div>
  );
};
